/*
 * Size Recommender — deterministic, client-side "Find my size" wizard (no LLM).
 *
 * No network beyond the size-guide chart that <product-size-guide> already
 * fetched. Body measurements never leave the browser. Instantiated by
 * product-size-guide.js, which passes itself in so the wizard can read the
 * parsed chart and open the size-chart modal. Config: size-recommender-config.js
 */
(function () {
  var CIRC = ['chest', 'waist', 'hip', 'thigh'];
  var STORAGE_KEY = 'gobi:sizeRecommender:v1';

  function normSize(s) { return s == null ? '' : String(s).trim().toUpperCase().replace(/\s+/g, ''); }
  // Canonical size key for MATCHING ONLY (display keeps the original token).
  // Folds separators so "L/XL", "L-XL", "L–XL" and "L XL" all compare equal —
  // the chart and the Shopify product often spell combined sizes differently.
  // Also folds X-run spellings (XXL ⇄ 2XL, XXS ⇄ 2XS, XXXL ⇄ 3XL): both exist
  // across charts/products and SIZE_ORD already declares them equal.
  function canonSize(s) {
    var t = normSize(s).replace(/[/\\.\-–—]+/g, '');
    return t.replace(/^(X{2,})([SL])$/, function (m, xs, sl) { return xs.length + 'X' + sl; });
  }
  // Universal size position on one scale (S=1) so different size systems and
  // combined sizes can be compared/snapped. Combined ("S/M","L-XL") -> the
  // average of its parts. Returns null for FREE-SIZE / numeric / kids tokens.
  var SIZE_ORD = { '2XS': -1, 'XXS': -1, 'XS': 0, 'S': 1, 'M': 2, 'L': 3, 'XL': 4, '2XL': 5, 'XXL': 5, '3XL': 6, 'XXXL': 6, '4XL': 7, '5XL': 8 };
  function sizeOrdinal(token) {
    var t = normSize(token);
    if (t === '') return null;
    if (SIZE_ORD[t] != null) return SIZE_ORD[t];
    var parts = t.split(/[/\-–—]/).map(normSize).filter(Boolean);
    if (parts.length >= 2) {
      var ords = parts.map(function (p) { return SIZE_ORD[p]; }).filter(function (o) { return o != null; });
      if (ords.length) return ords.reduce(function (a, b) { return a + b; }, 0) / ords.length;
    }
    return null;
  }
  // Fit standard = the letter closing the size block of a Gobi SKU
  // ("KW060291-1481S0000-004A" -> "A"); a trailing "-0971"-style segment is
  // ignored. Returns '' for SKUs that carry no such block.
  function skuStandard(sku) {
    var m = /-\d{3}([A-Z])(?:-|$)/.exec(String(sku == null ? '' : sku).trim().toUpperCase());
    return m ? m[1] : '';
  }
  function num(v) { var n = Number.parseFloat(String(v == null ? '' : v).replace(',', '.')); return Number.isFinite(n) ? n : null; }
  function esc(v) {
    return String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  // Shopify's `| t` filter HTML-escapes its output (e.g. ' -> &#39;), so the
  // values arriving in the i18n JSON are entity-encoded. Decode them once here
  // so esc()/textContent render real characters, not literal entities.
  function decodeEntities(str) {
    if (typeof str !== 'string' || str.indexOf('&') === -1) return str;
    var ta = document.createElement('textarea');
    ta.innerHTML = str;
    return ta.value;
  }
  // GTM funnel events. PII-FREE by design: never push height/weight/sex —
  // body data stays client-side; only size label / path / flags go out.
  function track(ev, params) {
    try {
      var payload = { event: ev };
      if (params) Object.keys(params).forEach(function (k) { payload[k] = params[k]; });
      (window.dataLayer = window.dataLayer || []).push(payload);
    } catch (e) { /* ignore */ }
  }

  function SizeRecommender(root, sizeGuide) {
    this.root = root;
    this.sizeGuide = sizeGuide;
    this.config = window.SIZE_RECOMMENDER_CONFIG || {};
    this.i18n = this.readJson('[data-sr-i18n]');
    this.shapeImg = this.readJson('[data-sr-shapes]'); // {gender-kind-option: asset url}
    this.gender = (root.dataset.gender || '').toLowerCase();

    this.overlay = root.querySelector('[data-sr-overlay]');
    this.dialog = root.querySelector('[data-sr-dialog]');
    this.dotsEl = root.querySelector('[data-sr-dots]');
    this.bodyEl = root.querySelector('[data-sr-body]');
    this.liveEl = root.querySelector('[data-sr-live]');
    this.viewChartBtn = root.querySelector('[data-sr-view-chart]');
    // captured BEFORE the root is portalled to <body> (open()) so the picker
    // marker can scope to this product's variant radios only.
    this.productInfo = root.closest('product-info');

    // market default (data-default-units from localization.country.unit_system);
    // a saved choice still wins via restore() below.
    this.unitSystem = (root.dataset.defaultUnits === 'imperial') ? 'imperial' : 'metric';
    this.inputs = {};            // raw input strings (height/weight; imperial height = height_ft + height_in)
    this.manualValues = {};      // raw manual measurement strings per part
    this.sex = (this.gender === 'female' || this.gender === 'male') ? this.gender : '';
    this._sexChosen = false;     // true only when the USER picked it (persist guard)
    this._restoredSex = '';
    this.mode = 'estimate';      // 'estimate' | 'manual'
    this.build = 'average';      // 'narrow'|'average'|'broad'  (shoulder/chest width)
    this.stomach = 'average';    // 'flat'|'average'|'round'    (midsection)
    this.stepIndex = 0;
    this.chart = null;
    this.applicable = false;
    this.result = null;
    this.selectedSize = '';
    this.openedBy = null;
    this._open = false;
    this._adding = false;

    this.onKeydown = this.onKeydown.bind(this);

    var self = this;
    root.querySelector('[data-sr-close]')?.addEventListener('click', this.close.bind(this));
    root.addEventListener('click', function (event) {
      if (event.target === root || event.target === self.overlay) self.close();
    });
    this.viewChartBtn?.addEventListener('click', function () {
      track('sr_view_chart');
      if (sizeGuide) sizeGuide.openChart('guide');
    });
    this.bodyEl?.addEventListener('click', this.onBodyClick.bind(this));

    // the theme re-renders the variant picker on variant change — re-apply the
    // recommended-size marker afterwards (subscribe/PUB_SUB_EVENTS from pubsub.js/global.js)
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, function () {
        if (self.result) { self.markRecommendedInPicker(); self.syncFormProps(); }
      });
    }
    // capture phase on the stable ancestor: runs before the product form's own
    // submit handler reads the form, whatever variant was picked last
    if (this.productInfo) {
      this.productInfo.addEventListener('submit', function () { if (self.result) self.syncFormProps(); }, true);
    }

    this.restore();
  }

  var P = SizeRecommender.prototype;

  P.readJson = function (sel) {
    var el = this.root.querySelector(sel);
    if (!el) return {};
    try {
      var obj = JSON.parse(el.textContent || '{}');
      Object.keys(obj).forEach(function (k) {
        if (typeof obj[k] === 'string') obj[k] = decodeEntities(obj[k]);
      });
      return obj;
    } catch (e) { return {}; }
  };
  P.t = function (key) { return this.i18n[key] != null ? this.i18n[key] : key; };
  P.tpl = function (key, vars) {
    var s = this.t(key);
    vars = vars || {};
    Object.keys(vars).forEach(function (k) { s = s.replace(new RegExp('\\[' + k + '\\]', 'g'), vars[k]); });
    return s;
  };
  P.isOpen = function () { return this._open; };
  P.hideLink = function () { if (this.sizeGuide && this.sizeGuide.openRecBtn) this.sizeGuide.openRecBtn.hidden = true; };

  // ---- chart ingestion -----------------------------------------------------
  P.matchPart = function (rawName) {
    var name = String(rawName || '').trim().toLowerCase();
    var syn = this.config.columnSynonyms || {};
    var keys = Object.keys(syn), i, j;
    for (i = 0; i < keys.length; i++) for (j = 0; j < syn[keys[i]].length; j++) if (name === syn[keys[i]][j]) return keys[i];
    for (i = 0; i < keys.length; i++) for (j = 0; j < syn[keys[i]].length; j++) if (name.indexOf(syn[keys[i]][j]) !== -1) return keys[i];
    return null;
  };

  P.inferGroup = function (present) {
    var has = function (p) { return present.indexOf(p) !== -1; };
    if (has('thigh') || (has('waist') && has('hip') && !has('chest'))) return 'bottom';
    if (has('chest') && has('waist') && has('hip')) return 'dress';
    return 'top';
  };

  P.applyChart = function (measurements, opts) {
    opts = opts || {};
    this.sizeOrder = (opts.sizeOrder || []).map(normSize);
    var cols = {}, refCols = {}, sizeSet = [];
    var self = this, flat2 = this.config.flatToCirc || 2;

    measurements.forEach(function (m) {
      if (!m || !Array.isArray(m.values)) return;
      var pk = self.matchPart(m.name);
      if (!pk) return;
      var isCirc = CIRC.indexOf(pk) !== -1;
      m.values.forEach(function (it) {
        var s = normSize(it && it.name); if (!s) return;
        var v = num(it && it.value); if (v == null) return;
        if (sizeSet.indexOf(s) === -1) sizeSet.push(s);
        if (isCirc) { (cols[pk] = cols[pk] || {})[s] = v; }
        else { (refCols[pk] = refCols[pk] || {})[s] = v; }
      });
    });

    var present = Object.keys(cols);
    if (!present.length) { this.applicable = false; this.hideLink(); return false; }

    var group = this.inferGroup(present);
    var evalParts = (this.config.evaluateParts && this.config.evaluateParts[group]) || present;
    var fallback = (this.config.fallbackWidthFrom && this.config.fallbackWidthFrom[group]) || {};
    var partValues = {}, parts = [], borrowed = {};

    evalParts.forEach(function (part) {
      var src = cols[part] ? part : (fallback[part] && cols[fallback[part]] ? fallback[part] : null);
      if (!src) return;
      var map = {};
      Object.keys(cols[src]).forEach(function (s) { map[s] = cols[src][s] * flat2; });
      partValues[part] = map; parts.push(part);
      if (src !== part) borrowed[part] = true; // garment width borrowed (boxy drape)
    });
    if (!parts.length) {
      present.forEach(function (part) {
        var map = {}; Object.keys(cols[part]).forEach(function (s) { map[s] = cols[part][s] * flat2; });
        partValues[part] = map; parts.push(part);
      });
    }
    if (!parts.length) { this.applicable = false; this.hideLink(); return false; }

    var ordered = this.sizeOrder.filter(function (s) { return sizeSet.indexOf(s) !== -1; });
    var extras = sizeSet.filter(function (s) { return ordered.indexOf(s) === -1; });

    this.chart = { group: group, parts: parts, partValues: partValues, refColumns: refCols, sizes: ordered.concat(extras), borrowed: borrowed };
    this.applicable = true;
    // the chart may list sizes this product doesn't actually carry (e.g. chart
    // has XS but the product is only S–XL) -> drop those so we never recommend
    // a size that isn't on the product.
    this.restrictToProductSizes();
    return true;
  };

  // Restrict recommendable sizes to those that exist as a size-option value on
  // the live Shopify product (from [data-all-variants]). Fails OPEN: if variant
  // data is missing or the size column can't be identified, keep all chart sizes.
  P.productSizeSet = function () {
    var all = this.variantsData();
    if (!all) return null;
    var ctx = this.matchContext(all), set = {};
    if (ctx.sizeIdx !== -1) {
      all.forEach(function (v) {
        if (v.options && v.options[ctx.sizeIdx] != null) set[canonSize(v.options[ctx.sizeIdx])] = true;
      });
    } else {
      // size option index unknown -> accept any option value matching a chart size
      var chartCanon = (this.chart ? this.chart.sizes : []).map(canonSize);
      all.forEach(function (v) {
        (v.options || []).forEach(function (o) {
          var c = canonSize(o);
          if (chartCanon.indexOf(c) !== -1) set[c] = true;
        });
      });
    }
    return set;
  };

  P.restrictToProductSizes = function () {
    if (!this.chart || !this.chart.sizes || !this.chart.sizes.length) return;
    var set = this.productSizeSet();
    if (!set) return;                 // unknown variant data -> don't filter
    if (!Object.keys(set).length) return;
    var filtered = this.chart.sizes.filter(function (s) { return set[canonSize(s)]; });
    if (filtered.length) this.chart.sizes = filtered;  // only narrow, never empty
  };

  // ---- fit standard (A / D) + per-product corrections ------------------------
  // See config.fitStandard / config.productFit. Read at compute time, never
  // cached: the SKU letters depend on the colour currently selected.
  P.guideHandle = function () {
    var ds = this.sizeGuide && this.sizeGuide.dataset;
    return String((ds && ds.handle) || '').trim().toLowerCase();
  };

  // The standard the product's CHART lists: "wk81a" -> A, anything else -> D
  // (a base chart and its "d" twin carry the same numbers).
  P.chartStandard = function () {
    return /^[a-z]+\d+a$/.test(this.guideHandle()) ? 'A' : 'D';
  };

  // size -> 'A' | 'D' for the sizes in the current colour. The SKU letter wins
  // when it is A or D; any other letter follows the chart.
  P.sizeStandards = function () {
    var out = {};
    if (!this.config.fitStandard || !this.chart) return out;
    var chartStd = this.chartStandard();
    var all = this.variantsData();
    var ctx = all ? this.matchContext(all) : null;
    var self = this;
    this.chart.sizes.forEach(function (size) {
      var std = chartStd;
      var vs = all ? self.variantsForSize(all, ctx, size) : [];
      for (var i = 0; i < vs.length; i++) {
        var letter = skuStandard(vs[i].sku);
        if (letter === 'A' || letter === 'D') { std = letter; break; }
      }
      out[size] = std;
    });
    return out;
  };

  // Where a size sits on the body-chart scale for THIS product's stock: a size
  // cut on the A standard wears smaller than its label, so it sits lower and
  // the same body reaches the next size up sooner.
  P.effOrd = function (size, stds) {
    var o = sizeOrdinal(size);
    if (o == null) return null;
    var cfg = this.config.fitStandard;
    var std = stds && stds[size];
    return o - ((cfg && cfg.shift && std && cfg.shift[std]) || 0);
  };

  P.productShift = function () {
    var map = this.config.productFit, h = this.guideHandle();
    var fit = (map && h && map[h]) || null;
    return (fit && typeof fit.shift === 'number') ? fit.shift : 0;
  };

  // Garment circumference for a part at a size. When the size's SKU standard
  // differs from the chart's, the chart is off by `gapCm` for that size: D
  // stock under an A chart is wider, A stock under a D chart narrower.
  P.garment = function (part, size, stds) {
    var values = this.chart.partValues[part];
    var v = values ? values[size] : null;
    if (v == null) return v;
    var cfg = this.config.fitStandard, std = stds && stds[size];
    if (cfg && cfg.gapCm && std && std !== this.chartStandard()) {
      v += (std === 'D' ? 1 : -1) * cfg.gapCm * (this.config.flatToCirc || 2);
    }
    return v;
  };

  // ---- open / close --------------------------------------------------------
  P.open = function (openedBy) {
    if (!this.sizeGuide) return;
    // Portal to <body> so the fixed overlay covers the full viewport regardless
    // of any transformed/positioned ancestor (the product section).
    if (this.root.parentElement !== document.body) document.body.appendChild(this.root);
    this.openedBy = openedBy || document.activeElement;
    this.root.hidden = false;
    this._open = true;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.onKeydown);
    if (typeof trapFocus === 'function') trapFocus(this.dialog || this.root, this.dialog);
    track('sr_open', { sr_product: window.location.pathname });

    var self = this;
    this.bodyEl.innerHTML = '<p class="sr-loading" role="status">' + esc(this.t('loading')) + '</p>';
    this.sizeGuide.ensureLoaded().then(function () {
      if (!self.applicable) { self.hideLink(); self.close(); return; }
      if (self.result) { self.showResult(); }
      else { self.mode = 'estimate'; self.stepIndex = 0; self.render(); }
    }).catch(function () {
      self.bodyEl.innerHTML = '<p class="sr-loading" role="alert">' + esc(self.t('error_load')) + '</p>';
    });
  };

  P.close = function () {
    if (!this._open) return;
    this.root.hidden = true;
    this._open = false;
    document.removeEventListener('keydown', this.onKeydown);
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openedBy);
    if (this.sizeGuide && this.sizeGuide.releaseBodyScroll) this.sizeGuide.releaseBodyScroll();
    else document.body.style.overflow = '';
  };

  P.onKeydown = function (event) {
    if (event.key === 'Escape') { event.preventDefault(); this.close(); }
  };

  // ---- step model ----------------------------------------------------------
  P.buildSteps = function () {
    // estimate path: height/weight -> Build -> Stomach. manual path: type your own.
    if (this.mode === 'manual') return [{ type: 'measurements' }, { type: 'manual' }];
    var steps = [{ type: 'measurements' }];
    // Build (shoulders/chest) & Stomach refine TOPS/dresses; they are irrelevant
    // to bottoms (waist/hip-driven), so pants/skirts skip them — size comes from
    // height/weight + the garment guard.
    if (!this.chart || this.chart.group !== 'bottom') {
      steps.push({ type: 'build' }, { type: 'stomach' });
    }
    return steps;
  };

  P.render = function () {
    this.steps = this.buildSteps();
    if (this.viewChartBtn) this.viewChartBtn.hidden = true;
    var step = this.steps[this.stepIndex];
    this.renderDots();
    if (!step) { this.showResult(); return; }
    if (step.type === 'measurements') this.renderMeasurements();
    else if (step.type === 'build') this.renderShape('build');
    else if (step.type === 'stomach') this.renderShape('stomach');
    else if (step.type === 'manual') this.renderManual();
    this.focusFirst();
  };

  P.renderDots = function () {
    if (!this.dotsEl) return;
    var total = this.steps.length, idx = this.stepIndex, html = '';
    if (total <= 1) { this.dotsEl.innerHTML = ''; this.dotsEl.hidden = true; return; }
    for (var i = 0; i < total; i++) html += '<span class="sr-dot' + (i === idx ? ' is-active' : '') + (i < idx ? ' is-done' : '') + '"></span>';
    this.dotsEl.innerHTML = html;
    this.dotsEl.hidden = false;
  };

  P.focusFirst = function () {
    var el = this.bodyEl.querySelector('input, button, [tabindex]');
    // Re-arm the focus trap on EVERY render: trapFocus snapshots first/last
    // focusable at call time, and each step swaps bodyEl.innerHTML — without
    // re-arming, Tab escapes the dialog into the PDP behind it.
    if (this._open && typeof trapFocus === 'function') trapFocus(this.dialog || this.root, el || this.dialog);
    else if (el) el.focus({ preventScroll: true });
  };

  // announce via the persistent live region (it sits OUTSIDE [data-sr-body] so
  // innerHTML swaps never destroy it; cleared first so repeats re-announce)
  P.announce = function (msg) {
    var el = this.liveEl;
    if (!el || !msg) return;
    el.textContent = '';
    window.requestAnimationFrame(function () { el.textContent = msg; });
  };

  // ---- step 1: measurements -----------------------------------------------
  P.renderMeasurements = function () {
    var im = this.unitSystem === 'imperial';
    var b = this.config.inputBounds || {};
    var hB = b.height || {}, wB = b.weight || {};
    var wUnit = im ? this.t('unit_lb') : this.t('unit_kg');
    var wMin = im && wB.min != null ? Math.round(wB.min / 0.45359) : wB.min;
    var wMax = im && wB.max != null ? Math.round(wB.max / 0.45359) : wB.max;
    // Body-type (sex) control is ALWAYS shown so the shopper can set/correct it
    // even on a gendered product (e.g. shopping as a gift); it preselects the
    // product's gender when known. Build/Stomach shape are asked on later steps.
    var sexHtml =
      '<div class="sr-field sr-field--sex"><span class="sr-label">' + esc(this.t('sex_legend')) + '</span>' +
      '<div class="sr-seg" data-sr-sex role="radiogroup" aria-label="' + esc(this.t('sex_legend')) + '">' +
        this.segBtn('sr-sex', 'female', this.t('sex_female'), this.sex === 'female') +
        this.segBtn('sr-sex', 'male', this.t('sex_male'), this.sex === 'male') +
      '</div></div>';
    var heightHtml;
    if (im) {
      // feet + inches pair — US shoppers don't know their height in raw inches
      heightHtml =
        '<div class="sr-field sr-field--pair"><span class="sr-label">' + esc(this.t('height_label')) + '</span>' +
        '<span class="sr-pair">' +
          '<span class="sr-input-wrap"><input type="number" inputmode="numeric" step="1" min="3" max="7" data-sr-in="height_ft" value="' + esc(this.inputs.height_ft || '') + '" autocomplete="off" aria-label="' + esc(this.t('height_label')) + ' (' + esc(this.t('unit_ft')) + ')"><span class="sr-unit">' + esc(this.t('unit_ft')) + '</span></span>' +
          '<span class="sr-input-wrap"><input type="number" inputmode="decimal" step="any" min="0" max="11.5" data-sr-in="height_in" value="' + esc(this.inputs.height_in || '') + '" autocomplete="off" aria-label="' + esc(this.t('height_label')) + ' (' + esc(this.t('unit_in')) + ')"><span class="sr-unit">' + esc(this.t('unit_in')) + '</span></span>' +
        '</span></div>';
    } else {
      heightHtml = this.fieldRow('height', this.t('height_label'), this.t('unit_cm'), this.inputs.height, hB.min, hB.max);
    }
    this.bodyEl.innerHTML =
      '<h3 class="sr-step-title">' + esc(this.t('step1_title')) + '</h3>' +
      '<div class="sr-seg sr-seg--units" data-sr-units role="radiogroup" aria-label="' + esc(this.t('unit_toggle')) + '">' +
        this.segBtn('sr-unit', 'imperial', this.t('imperial'), im) +
        this.segBtn('sr-unit', 'metric', this.t('metric'), !im) +
      '</div>' +
      sexHtml +
      '<div class="sr-measure-list">' +
        heightHtml +
        this.fieldRow('weight', this.t('weight_label'), wUnit, this.inputs.weight, wMin, wMax) +
      '</div>' +
      '<p class="sr-error" data-sr-error role="alert" hidden></p>' +
      '<div class="sr-actions"><button type="button" class="sr-skip" data-sr-skip>' + esc(this.t('skip_link')) + ' <span class="sr-arrow" aria-hidden="true">&rarr;</span></button>' +
      '<button type="button" class="button sr-next" data-sr-next>' + esc(this.t('next')) + '</button></div>';

    this.bindSeg('[data-sr-units]', (function (val) {
      if (val !== this.unitSystem) {
        this.captureMeasurements();
        this.convertUnits(this.unitSystem, val);
        this.unitSystem = val;
      }
      this.renderMeasurements();
    }).bind(this));
    this.bindSeg('[data-sr-sex]', (function (val) { this.sex = val; this._sexChosen = true; }).bind(this));
  };

  P.fieldRow = function (key, label, unit, value, min, max) {
    var bounds = ' step="any"';
    if (min != null) bounds += ' min="' + min + '"';
    if (max != null) bounds += ' max="' + max + '"';
    return '<label class="sr-field"><span class="sr-label">' + esc(label) + '</span>' +
      '<span class="sr-input-wrap"><input type="number" inputmode="decimal"' + bounds + ' data-sr-in="' + key + '" value="' + esc(value || '') + '" autocomplete="off">' +
      '<span class="sr-unit">' + esc(unit) + '</span></span></label>';
  };

  P.captureMeasurements = function () {
    var self = this;
    ['height', 'weight', 'height_ft', 'height_in'].forEach(function (k) {
      var el = self.bodyEl.querySelector('[data-sr-in="' + k + '"]');
      if (el) self.inputs[k] = el.value;
    });
  };

  // Convert the typed values when the unit system changes so "175 / 80" never
  // gets silently reinterpreted as 175 in / 80 lb (a wrong-size trap: 80 lb is
  // a VALID weight). Always converts via metric; rounds for display only.
  P.convertUnits = function (from, to) {
    if (from === to) return;
    var self = this;
    if (to === 'metric') {
      var ft = num(this.inputs.height_ft), inch = num(this.inputs.height_in);
      if (ft != null || inch != null) {
        this.inputs.height = String(Math.round(((ft || 0) * 12 + (inch || 0)) * 2.54));
      }
      var lb = num(this.inputs.weight);
      if (lb != null) this.inputs.weight = String(Math.round(lb * 0.45359 * 10) / 10);
    } else {
      var cm = num(this.inputs.height);
      if (cm != null) {
        var totalIn = cm / 2.54;
        var f = Math.floor(totalIn / 12);
        var rest = Math.round((totalIn - f * 12) * 2) / 2; // half-inch steps
        if (rest >= 12) { f += 1; rest = 0; }
        this.inputs.height_ft = String(f);
        this.inputs.height_in = String(rest);
      }
      var kg = num(this.inputs.weight);
      if (kg != null) this.inputs.weight = String(Math.round(kg / 0.45359));
    }
    Object.keys(this.manualValues).forEach(function (k) {
      var v = num(self.manualValues[k]);
      if (v == null) return;
      v = (to === 'metric') ? v * 2.54 : v / 2.54;
      self.manualValues[k] = String(Math.round(v * 10) / 10);
    });
  };

  // ---- step: manual measurements ------------------------------------------
  P.renderManual = function () {
    var self = this, im = this.unitSystem === 'imperial', unit = im ? this.t('unit_in') : this.t('unit_cm');
    var rows = this.chart.parts.map(function (part) {
      return self.fieldRow('m_' + part, self.t('part_' + part), unit, self.manualValues[part]);
    }).join('');
    this.bodyEl.innerHTML =
      '<h3 class="sr-step-title">' + esc(this.t('manual_title')) + '</h3>' +
      '<p class="sr-step-intro">' + esc(this.t('manual_intro')) + '</p>' + rows +
      '<p class="sr-error" data-sr-error role="alert" hidden></p>' +
      '<div class="sr-actions"><button type="button" class="sr-back" data-sr-back><span class="sr-arrow" aria-hidden="true">&larr;</span> ' + esc(this.t('back')) + '</button>' +
      '<button type="button" class="button sr-next" data-sr-compute>' + esc(this.t('next')) + '</button></div>';
  };

  // ---- step: body shape (Build / Stomach) ----------------------------------
  P.renderShape = function (kind) {
    var self = this;
    var opts = kind === 'build' ? ['narrow', 'average', 'broad'] : ['flat', 'average', 'round'];
    var current = kind === 'build' ? this.build : this.stomach;
    var cards = opts.map(function (o) {
      var on = current === o;
      return '<button type="button" class="sr-shape__card' + (on ? ' is-selected' : '') + '" role="radio" aria-checked="' + (on ? 'true' : 'false') + '" data-sr-shape="' + kind + '" data-sr-val="' + o + '">' +
        '<span class="sr-shape__img">' + self.shapeMedia(kind, o) + '</span>' +
        '<span class="sr-shape__label">' + esc(self.t(kind + '_' + o)) + '</span>' +
      '</button>';
    }).join('');
    this.bodyEl.innerHTML =
      '<h3 class="sr-step-title">' + esc(this.t(kind + '_title')) + '</h3>' +
      '<div class="sr-shape" role="radiogroup" aria-label="' + esc(this.t(kind + '_title')) + '">' + cards + '</div>' +
      '<p class="sr-error" data-sr-error role="alert" hidden></p>' +
      '<div class="sr-actions"><button type="button" class="sr-back" data-sr-back><span class="sr-arrow" aria-hidden="true">&larr;</span> ' + esc(this.t('back')) + '</button>' +
      '<button type="button" class="button sr-next" data-sr-advance>' + esc(this.t('next')) + '</button></div>';
    this.bindShape(kind);
  };

  P.bindShape = function (kind) {
    var self = this;
    var cards = Array.prototype.slice.call(this.bodyEl.querySelectorAll('[data-sr-shape]'));
    if (!cards.length) return;
    function select(card) {
      cards.forEach(function (c) {
        var on = c === card;
        c.classList.toggle('is-selected', on);
        c.setAttribute('aria-checked', on ? 'true' : 'false');
        c.tabIndex = on ? 0 : -1;
      });
      var val = card.getAttribute('data-sr-val');
      if (kind === 'build') self.build = val; else self.stomach = val;
    }
    var initial = cards.filter(function (c) { return c.getAttribute('aria-checked') === 'true'; })[0] || cards[0];
    cards.forEach(function (c) { c.tabIndex = (c === initial) ? 0 : -1; });
    cards.forEach(function (card, idx) {
      card.addEventListener('click', function () { select(card); });
      card.addEventListener('keydown', function (event) {
        var dir = (event.key === 'ArrowRight' || event.key === 'ArrowDown') ? 1
                : (event.key === 'ArrowLeft' || event.key === 'ArrowUp') ? -1 : 0;
        if (!dir) return;
        event.preventDefault();
        var next = cards[(idx + dir + cards.length) % cards.length];
        next.focus(); select(next);
      });
    });
  };

  // Gender-matched illustration for the option (male/female set chosen by the
  // selected body type); falls back to the inline silhouette if the image map
  // is absent. alt="" — the visible card label is the accessible name.
  P.shapeMedia = function (kind, opt) {
    var key = (this.sex === 'female' ? 'female' : 'male') + '-' + kind + '-' + opt;
    var url = this.shapeImg && this.shapeImg[key];
    if (url) return '<img class="sr-shape__pic" src="' + esc(url) + '" alt="" loading="lazy" width="120" height="120">';
    return this.shapeSvg(kind, opt);
  };

  // Inline line-art silhouette fallback (inherit currentColor). Build = front
  // torso with varying shoulder width; Stomach = side profile with varying belly.
  P.shapeSvg = function (kind, opt) {
    function svg(inner) {
      return '<svg viewBox="0 0 100 88" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round" aria-hidden="true" focusable="false">' + inner + '</svg>';
    }
    if (kind === 'build') {
      var s = opt === 'narrow' ? 18 : (opt === 'broad' ? 33 : 25); // shoulder half-width
      var L = 50 - s, R = 50 + s, wl = 50 - s * 0.6, wr = 50 + s * 0.6;
      return svg(
        '<circle cx="50" cy="15" r="10"/>' +
        '<path d="M' + L + ' 42 Q' + L + ' 30 50 28 Q' + R + ' 30 ' + R + ' 42 L' + wr + ' 80 L' + wl + ' 80 Z"/>'
      );
    }
    var b = opt === 'flat' ? 2 : (opt === 'round' ? 18 : 9); // belly bulge
    return svg(
      '<circle cx="44" cy="15" r="10"/>' +
      '<path d="M40 28 Q' + (34 - b) + ' 52 40 80 L60 80 Q60 52 56 28 Z"/>'
    );
  };

  // ---- click delegation ----------------------------------------------------
  P.onBodyClick = function (event) {
    var t = event.target.closest('button');
    if (!t || !this.bodyEl.contains(t)) return;

    if (t.hasAttribute('data-sr-next')) { this.captureMeasurements(); if (this.validateMeasurements()) this.advance(); return; }
    if (t.hasAttribute('data-sr-advance')) { this.advance(); return; }
    if (t.hasAttribute('data-sr-skip')) { this.captureMeasurements(); this.mode = 'manual'; this.stepIndex = 1; track('sr_manual_path'); this.render(); return; }
    if (t.hasAttribute('data-sr-back')) { this.back(); return; }
    if (t.hasAttribute('data-sr-compute')) { if (this.captureManual()) this.compute(); return; }
    if (t.hasAttribute('data-sr-edit')) { this.mode = 'estimate'; this.stepIndex = 0; this.render(); return; }
    if (t.hasAttribute('data-sr-restart')) { this.restart(); return; }
    if (t.hasAttribute('data-sr-addcart')) { this.addToCart(); return; }
    if (t.hasAttribute('data-sr-continue')) { this.close(); return; }
  };

  P.advance = function () {
    if (this.stepIndex >= this.steps.length - 1) { this.compute(); return; }
    this.stepIndex++;
    this.render();
  };

  P.back = function () {
    if (this.mode === 'manual') { this.mode = 'estimate'; this.stepIndex = 0; this.render(); return; }
    if (this.stepIndex > 0) { this.stepIndex--; this.render(); }
  };

  // ---- validation / input reading -----------------------------------------
  P.setError = function (msg) {
    var el = this.bodyEl.querySelector('[data-sr-error]');
    if (el) { el.textContent = msg; el.hidden = false; }
    this.announce(msg);
  };

  // height in cm from the current inputs (imperial = ft + in pair)
  P.heightCm = function () {
    if (this.unitSystem !== 'imperial') return num(this.inputs.height);
    var ft = num(this.inputs.height_ft), inch = num(this.inputs.height_in);
    if (ft == null && inch == null) return null;
    return ((ft || 0) * 12 + (inch || 0)) * 2.54;
  };

  P.toMetric = function (heightRaw, weightRaw) {
    var h = this.unitSystem === 'imperial' ? this.heightCm() : num(heightRaw);
    var w = num(weightRaw);
    if (this.unitSystem === 'imperial' && w != null) w = w * 0.45359;
    return { h: h, w: w };
  };

  P.validateMeasurements = function () {
    var b = this.config.inputBounds || {};
    if (!this.sex) { this.setError(this.t('sex_required')); return false; }
    var conv = this.toMetric(this.inputs.height, this.inputs.weight);
    if (conv.h == null) { this.setError(this.t('error_required')); return false; }
    if (conv.w == null) { this.setError(this.t('error_required')); return false; }
    if (b.height && (conv.h < b.height.min || conv.h > b.height.max)) {
      this.setError(this.rangeMsg(b.height, 'height')); return false;
    }
    if (b.weight && (conv.w < b.weight.min || conv.w > b.weight.max)) {
      this.setError(this.rangeMsg(b.weight, 'weight')); return false;
    }
    return true;
  };

  P.rangeMsg = function (bound, kind) {
    // bounds are stored in metric; show them WITH units, in the unit system the
    // user is typing (imperial height as "X ft Y in" to match the paired inputs)
    var self = this;
    if (this.unitSystem !== 'imperial') {
      var u = kind === 'weight' ? this.t('unit_kg') : this.t('unit_cm');
      return this.tpl('error_range', { min: bound.min + ' ' + u, max: bound.max + ' ' + u });
    }
    if (kind === 'weight') {
      var lb = this.t('unit_lb');
      return this.tpl('error_range', {
        min: Math.round(bound.min / 0.45359) + ' ' + lb,
        max: Math.round(bound.max / 0.45359) + ' ' + lb
      });
    }
    var fmt = function (cm) {
      var t = cm / 2.54, ft = Math.floor(t / 12), inch = Math.round(t - ft * 12);
      if (inch >= 12) { ft += 1; inch = 0; }
      return ft + ' ' + self.t('unit_ft') + ' ' + inch + ' ' + self.t('unit_in');
    };
    return this.tpl('error_range', { min: fmt(bound.min), max: fmt(bound.max) });
  };

  P.captureManual = function () {
    var self = this, ok = true;
    this.chart.parts.forEach(function (part) {
      var el = self.bodyEl.querySelector('[data-sr-in="m_' + part + '"]');
      if (el) self.manualValues[part] = el.value;
    });
    this.chart.parts.forEach(function (part) {
      if (num(self.manualValues[part]) == null) ok = false;
    });
    if (!ok) this.setError(this.t('error_required'));
    return ok;
  };

  // ---- estimation + scoring -----------------------------------------------
  P.estimateBody = function () {
    var conv = this.toMetric(this.inputs.height, this.inputs.weight);
    var anthro = (this.config.anthro || {})[this.sex] || {};
    var refH = anthro.refH || 170;
    var body = {};
    this.chart.parts.forEach(function (part) {
      var c = anthro[part]; if (!c) { body[part] = null; return; }
      body[part] = c.base + c.wCoef * conv.w + c.hCoef * (conv.h - refH);
    });
    return body;
  };

  P.readManualBody = function () {
    var self = this, body = {}, im = this.unitSystem === 'imperial';
    this.chart.parts.forEach(function (part) {
      var v = num(self.manualValues[part]);
      body[part] = v == null ? null : (im ? v * 2.54 : v);
    });
    return body;
  };

  P.bandFor = function (group, part) {
    var cats = this.config.categories || {};
    return (cats[group] && cats[group][part]) || (cats.top && cats.top[part]) || { ideal: 8, tight: 2, loose: 20, weight: 0.4 };
  };

  P.scoreSizes = function (body) {
    var self = this, group = this.chart.group;
    var idealAdj = (this.config.easeAdjustBySex && this.config.easeAdjustBySex[this.sex]) || 0;
    var stds = this.sizeStandards();
    // a style that runs small/large by productShift sizes wears like a chart
    // that is one grade (cmPerSize, flat) per size narrower/wider — this is how
    // the measured-body path sees config.productFit
    var wears = this.productShift() * (this.config.cmPerSize || 3) * (this.config.flatToCirc || 2);
    var results = this.chart.sizes.map(function (size) {
      var byPart = {}, total = 0, allPerfect = true, n = 0;
      self.chart.parts.forEach(function (part) {
        var garment = self.garment(part, size, stds), bodyVal = body[part];
        if (garment == null || bodyVal == null) return;
        garment -= wears;
        var band = self.bandFor(group, part), ease = garment - bodyVal;
        var ideal = band.ideal + idealAdj;
        // "soft" parts (boxy-borrowed widths, or stretchy waistbands) only flag
        // tight when the body exceeds the garment; looseness is normal drape, so
        // they use the wide wearability band. Primary parts use a narrow window
        // around the ideal ease so adjacent sizes differentiate (bigger=loose,
        // smaller=tight). Ranking always uses distance to ideal.
        var soft = (self.chart.borrowed && self.chart.borrowed[part]) || band.soft === true;
        var verdict;
        if (soft) {
          verdict = ease < band.tight ? 'tight' : (ease > band.loose ? 'loose' : 'perfect');
        } else {
          var win = (self.config.verdictWindow != null) ? self.config.verdictWindow : 3;
          verdict = ease < ideal - win ? 'tight' : (ease > ideal + win ? 'loose' : 'perfect');
        }
        if (verdict !== 'perfect') allPerfect = false;
        var scale = (band.loose - band.tight) / 2 || 6;
        // ranking must agree with the verdict: soft parts are penalised ONLY for
        // being too tight (loose drape is fine), primary parts symmetrically
        // around the ideal. Otherwise an all-perfect larger size could lose to a
        // smaller size that's tight on the primary part.
        var pen = soft
          ? (ease < band.tight ? Math.pow((ease - band.tight) / scale, 2) : 0)
          : Math.pow((ease - ideal) / scale, 2);
        total += band.weight * pen;
        byPart[part] = { ease: ease, verdict: verdict, band: band };
        n++;
      });
      return { size: size, total: total, byPart: byPart, n: n, allPerfect: allPerfect && n > 0 };
    });
    // A size with NO measured parts keeps total=0 and would otherwise always
    // "win" (e.g. a 3XL listed only in the chart's Body-length column) — rank
    // only sizes that have fit data; fall back to all if none do.
    var pool = results.filter(function (r) { return r.n > 0; });
    if (!pool.length) pool = results;
    var tieUp = (this.config.tieBreak || 'up') === 'up', best = pool[0];
    for (var i = 1; i < pool.length; i++) {
      var r = pool[i], d = r.total - best.total;
      if (d < -1e-6) { best = r; continue; }
      if (Math.abs(d) <= 1e-6) {
        if (r.allPerfect && !best.allPerfect) { best = r; continue; }
        if (r.allPerfect === best.allPerfect) best = tieUp ? r : best;
      }
    }
    return { sizes: results, recommended: best.size, group: group };
  };

  // ---- height/weight -> size via the brand's body chart (central anchor) ----
  // WEIGHT drives the size (piecewise-linear interpolation of the size ordinal
  // from the per-size weight midpoints); HEIGHT applies only a small, correctly-
  // signed correction (taller -> slightly SMALLER, mirroring the anthro hCoef).
  // Result is clamped to one size-step beyond the chart's own range.
  // `plain` leaves out the per-product correction (config.productFit), so the
  // caller can tell whether that correction is what moved the size.
  P.bodyTargetOrdinal = function (sex, h, w, unclamped, plain) {
    var cfg = this.config.heightWeightChart;
    if (!cfg) return null;
    var rows = cfg[sex];
    if (!rows || !rows.length) return null;
    var pts = rows.map(function (r) {
      // wlo via Math.min so a high-first authored range can't flip the guard
      return { ord: sizeOrdinal(r.size), hmid: (r.h[0] + r.h[1]) / 2, wmid: (r.w[0] + r.w[1]) / 2, wlo: Math.min(r.w[0], r.w[1]) };
    }).filter(function (p) { return p.ord != null; });
    if (pts.length < 2) return null;
    pts.sort(function (a, b) { return a.ord - b.ord; });
    function interpW(val) {
      for (var i = 0; i < pts.length - 1; i++) {
        var a = pts[i], b = pts[i + 1];
        if (val < b.wmid || i === pts.length - 2) {
          var span = (b.wmid - a.wmid) || 1;
          return a.ord + (val - a.wmid) / span * (b.ord - a.ord);
        }
      }
      return pts[0].ord;
    }
    var target = interpW(w);
    // small, correctly-signed height correction: at the same weight a taller body
    // is leaner -> a slightly SMALLER size (subtract for h above the mean height).
    var hk = (cfg.heightCoef != null) ? cfg.heightCoef : 0;
    if (hk && h != null) {
      var refH = pts.reduce(function (s, p) { return s + p.hmid; }, 0) / pts.length;
      target += hk * (refH - h);
    }
    // per-sex up/down nudge (object {female,male}); a plain number = both sexes
    var sb = cfg.sizeBias;
    target += (sb && typeof sb === 'object') ? (sb[sex] || 0) : (typeof sb === 'number' ? sb : 0);
    // body-shape nudge: Build (shoulder/chest) + Stomach (midsection) shift the
    // size; 'average' = 0 so the brand anchors hold (see config.bodyShape). Only
    // for tops/dresses — bottoms are waist/hip-driven and ignore these (also
    // guards against a build/stomach value persisted from a previous top).
    var bsh = this.config.bodyShape;
    if (bsh && (!this.chart || this.chart.group !== 'bottom')) {
      if (bsh.build && bsh.build[this.build] != null) target += bsh.build[this.build];
      if (bsh.stomach && bsh.stomach[this.stomach] != null) target += bsh.stomach[this.stomach];
    }
    // per-product correction from returns: this style runs small (+) / large (-)
    if (!plain) target += this.productShift();
    // Allow ONE size-step of linear extrapolation beyond the brand chart so
    // XS/2XS and 3XL stay reachable for bodies outside the published S..2XL
    // ranges (sizeFromHeightWeight only snaps to sizes the product carries,
    // so this can never invent a size).
    // boundaryHint needs the raw body position: the below-chart floor and the
    // one-step clamp below are FLOORS, not body positions, and captioning from
    // a floored value invents "between X and Y" claims (see boundaryHint).
    if (unclamped) return target;
    // BELOW-chart extrapolation must be EARNED, not granted on weight alone:
    // garment length/sleeves shrink with the size, so a shopper who is merely
    // a couple of kg under the smallest row keeps that row (return IN74363:
    // 166cm/50kg got extrapolated to XS and it ran too small — now S). The
    // allowed drop below the smallest row fades in on EITHER axis, whichever
    // is stronger: height below the smallest row's height midpoint (petite
    // frames genuinely fit the shorter sub-chart garment), or weight below
    // the smallest row's PUBLISHED low bound (a full weight-step under it is
    // a genuinely sub-chart body at any height — pinning e.g. 165cm/40kg to S
    // would contradict the fit model with no escape hatch). Each deficit is
    // scaled by the chart's own per-size step so the boundary stays a weight/
    // height boundary like every other in the chart (at 166cm the female
    // XS/S line lands near 48kg). In-chart and above-chart targets are
    // unaffected, so the brand anchors hold.
    var minOrd = pts[0].ord - 1;
    if (cfg.belowChartHeightGuard && h != null) {
      var steps = Math.max(1, pts[pts.length - 1].ord - pts[0].ord);
      var hStep = (pts[pts.length - 1].hmid - pts[0].hmid) / steps;
      var wStep = (pts[pts.length - 1].wmid - pts[0].wmid) / steps;
      var dropH = hStep > 0 ? (pts[0].hmid - h) / hStep : 0;
      var dropW = (w != null && wStep > 0) ? (pts[0].wlo - w) / wStep : 0;
      var drop = Math.max(0, Math.min(1, Math.max(dropH, dropW)));
      minOrd = pts[0].ord - drop;
    }
    return Math.max(minOrd, Math.min(pts[pts.length - 1].ord + 1, target));
  };

  // Which gendered body chart sets the SIZE SCALE. The garment is cut to the
  // PRODUCT's gender, so on a gendered product the size follows that gender (a
  // 180/80 woman and a 180/80 man have near-identical girth -> the same size in
  // the same men's sweater). The body-type toggle still drives body proportions
  // (estimateBody) and matters for size only on unisex/unknown products.
  P.sizingSex = function () {
    if (this.gender === 'female' || this.gender === 'male') return this.gender;
    return this.sex;
  };

  // Resolve height/weight -> the nearest AVAILABLE product size (by ordinal;
  // a size cut on the A standard counts as sitting lower, see effOrd).
  P.sizeFromHeightWeight = function (plain) {
    if (!this.chart || !this.chart.sizes || !this.chart.sizes.length) return null;
    var conv = this.toMetric(this.inputs.height, this.inputs.weight);
    if (conv.h == null || conv.w == null) return null;
    var target = this.bodyTargetOrdinal(this.sizingSex(), conv.h, conv.w, false, plain);
    if (target == null) return null;
    var self = this, stds = this.sizeStandards();
    // Snap to the nearest available size; honour config.tieBreak on exact ties.
    // The hw path previously kept the first-iterated size at the min distance,
    // which rounded x.5 ties DOWN regardless of tieBreak — cancelling the
    // sizeBias up-nudge in exactly the borderline cases it was meant to lift.
    var tieUp = (this.config.tieBreak || 'up') === 'up';
    var best = null, bestDist = Infinity, bestOrd = null;
    this.chart.sizes.forEach(function (s) {
      var o = self.effOrd(s, stds);
      if (o == null) return;
      var d = Math.abs(o - target);
      if (d < bestDist - 1e-6) { bestDist = d; best = s; bestOrd = o; }
      else if (Math.abs(d - bestDist) <= 1e-6 && best != null) {
        if (tieUp ? (o > bestOrd) : (o < bestOrd)) { best = s; bestOrd = o; }
      }
    });
    return best; // null if no available size has a known ordinal (e.g. FREE-SIZE)
  };

  // The binding fit point for the garment group: the highest-weight part that is
  // a real chart column (not a borrowed/soft drape part) — chest on tops/dresses,
  // hip on bottoms. Used to decide whether the body fits the body-chart size.
  P.primaryPart = function () {
    if (!this.chart || !this.chart.parts) return null;
    var self = this, group = this.chart.group, best = null, bestW = -1;
    this.chart.parts.forEach(function (p) {
      var band = self.bandFor(group, p);
      var soft = (self.chart.borrowed && self.chart.borrowed[p]) || band.soft === true;
      if (soft) return;
      if (band.weight > bestW) { bestW = band.weight; best = p; }
    });
    if (best) return best;
    this.chart.parts.forEach(function (p) {
      var band = self.bandFor(group, p);
      if (band.weight > bestW) { bestW = band.weight; best = p; }
    });
    return best;
  };

  // Product-cut adjustment: nudge the body-chart size by at most cutClampSteps
  // when THIS product's primary-part garment ease at the baseline size falls
  // outside the wearable band — too tight (body bigger than the garment can
  // stretch to) -> size up; far too loose (boxy/oversized) -> size down. Regular
  // cuts sit inside the band and are returned unchanged, so the brand anchor holds.
  P.adjustForGarment = function (baseSize, body) {
    if (!this.chart || !this.chart.parts || !this.chart.parts.length) return baseSize;
    var part = this.primaryPart();
    if (!part) return baseSize;
    var bodyVal = body ? body[part] : null;
    if (!this.chart.partValues[part] || bodyVal == null) return baseSize;
    var self = this, stds = this.sizeStandards(), values = {};
    this.chart.sizes.forEach(function (s) { values[s] = self.garment(part, s, stds); });
    var band = this.bandFor(this.chart.group, part);
    var sizes = this.chart.sizes
      .filter(function (s) { return sizeOrdinal(s) != null && values[s] != null; })
      .sort(function (a, b) { return sizeOrdinal(a) - sizeOrdinal(b); });
    if (!sizes.length) return baseSize;
    var idx = sizes.indexOf(baseSize);
    if (idx === -1) {
      var bc = canonSize(baseSize);
      for (var k = 0; k < sizes.length; k++) { if (canonSize(sizes[k]) === bc) { idx = k; break; } }
    }
    if (idx === -1) return baseSize;
    var maxSteps = (this.config.cutClampSteps != null) ? this.config.cutClampSteps : 1;
    var cur = idx, steps = 0;
    while (steps < maxSteps) {
      var ease = values[sizes[cur]] - bodyVal;
      if (ease < band.tight && cur < sizes.length - 1) { cur++; steps++; continue; } // too tight -> up
      if (ease > band.loose && cur > 0) { cur--; steps++; continue; }                // too loose -> down
      break;
    }
    return sizes[cur];
  };

  // Estimate-mode recommendation. The brand body chart (height/weight) sets the
  // CENTRAL size — it matches the brand's published sizing, which garment ease
  // alone can't reproduce (men's/women's charts use very different absolute
  // measurements). The product's own garment chart then nudges that size up/down
  // for unusually slim/boxy cuts (adjustForGarment). Falls back to the garment-
  // ease pick only when there is no usable body chart (e.g. FREE-SIZE tokens).
  P.estimateModeSize = function (body, easeFallback) {
    var baseSize = this.sizeFromHeightWeight();
    var size, basis;
    if (!baseSize) {
      if (!easeFallback) return null;
      size = easeFallback; basis = 'fit';
    } else {
      // A per-product correction (config.productFit) that already moved the size
      // is final — returns outrank the chart; the garment guard only acts when
      // the correction left the size where the body chart put it.
      var corrected = this.productShift() !== 0 && baseSize !== this.sizeFromHeightWeight(true);
      var adjusted = corrected ? baseSize : this.adjustForGarment(baseSize, body);
      size = adjusted; basis = (adjusted !== baseSize) ? 'fit' : 'hw';
    }
    // (Build/Stomach already shifted the chart target inside bodyTargetOrdinal.)
    return { size: size, basis: basis };
  };

  // Scores + stores the recommendation and updates the PDP (trigger label,
  // picker marker, chart-table row) WITHOUT opening/altering the modal — shared
  // by the wizard and the page-load auto-compute.
  P.computeResult = function () {
    var body = this.mode === 'manual' ? this.readManualBody() : this.estimateBody();
    this.body = body;
    var scored = this.scoreSizes(body);
    scored.basis = 'fit';
    // Estimate mode: brand body chart (weight-driven) = central size; the product's
    // garment chart nudges it ±cutClampSteps for slim/boxy cuts. Manual mode keeps
    // the per-part garment-ease pick from scoreSizes above.
    if (this.mode === 'estimate') {
      var pick = this.estimateModeSize(body, scored.recommended);
      if (pick) { scored.recommended = pick.size; scored.basis = pick.basis; }
    }
    this.result = scored;
    // selectedSize is the PRODUCT's real label (e.g. "L/XL"), not the chart token
    // (e.g. "L-XL") — used for display, cart and attribution.
    this.selectedSize = this.displayLabelFor(scored.recommended);
    if (this.sizeGuide && this.sizeGuide.setRecommendedLabel) {
      this.sizeGuide.setRecommendedLabel(this.tpl('your_size_is', { size: this.selectedSize }));
    }
    this.markRecommendedInPicker();
    this.syncFormProps();
    return scored;
  };

  P.compute = function () {
    var scored = this.computeResult();
    this.persist();
    this.showResult();
    track('sr_result', {
      sr_size: this.selectedSize,
      sr_basis: scored.basis,
      sr_group: this.chart ? this.chart.group : '',
      sr_mode: this.mode,
      sr_oos: !!(this.availability && this.availability[scored.recommended] === false)
    });
  };

  // Auto-compute from the saved profile so a returning visitor sees
  // "Your size is X" on the trigger link with zero interaction. Purely visual:
  // no modal, no persistence, nothing added to cart; silent on any miss.
  P.maybeAutoCompute = function () {
    if (this.result || !this.applicable || this.mode !== 'estimate') return;
    if (this.sex !== 'female' && this.sex !== 'male') return;
    var b = this.config.inputBounds || {};
    var conv = this.toMetric(this.inputs.height, this.inputs.weight);
    if (conv.h == null || conv.w == null) return;
    if (b.height && (conv.h < b.height.min || conv.h > b.height.max)) return;
    if (b.weight && (conv.w < b.weight.min || conv.w > b.weight.max)) return;
    var scored = this.computeResult();
    track('sr_result', { sr_auto: true, sr_size: this.selectedSize, sr_basis: scored.basis, sr_group: this.chart ? this.chart.group : '', sr_mode: this.mode });
  };

  // Mark the recommended size on the PDP's size radios (CSS class on the label
  // + SR-only suffix). Scoped to this product's <product-info>; re-applied on
  // variantChange because the theme re-renders the picker.
  P.markRecommendedInPicker = function () {
    if (!this.result || !this.selectedSize) return;
    var scope = this.productInfo || document;
    var inputs = scope.querySelectorAll('.variant-option-radio-input');
    if (!inputs.length) return;
    var target = canonSize(this.selectedSize), recToken = canonSize(this.result.recommended);
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      var val = input.getAttribute('aria-label') || input.value || '';
      var canon = canonSize(val);
      var match = canon !== '' && (canon === target || canon === recToken);
      var label = (input.labels && input.labels[0]) || null;
      if (!label) continue;
      label.classList.toggle('is-sr-recommended', match);
      var mark = label.querySelector('[data-sr-rec-mark]');
      if (match && !mark) {
        mark = document.createElement('span');
        mark.className = 'visually-hidden';
        mark.setAttribute('data-sr-rec-mark', '');
        mark.textContent = ' (' + this.t('recommended_size') + ')';
        label.appendChild(mark);
      } else if (!match && mark) {
        mark.parentNode.removeChild(mark);
      }
    }
  };

  // ---- result --------------------------------------------------------------
  P.showResult = function () {
    if (this.dotsEl) this.dotsEl.hidden = true;
    // only offer "View size chart" when a chart modal is actually rendered
    // (the Size Guide toggle may be off while Find my size is on)
    if (this.viewChartBtn) this.viewChartBtn.hidden = !(this.sizeGuide && this.sizeGuide.chartModal);
    this.renderResult(this.result, this.selectedSize);
    // move focus to the result container (tabindex=-1) and re-arm the trap
    this.focusFirst();
  };

  // When the body sits ~midway between two available sizes (the height/weight
  // target's fractional position is near 0.5), surface BOTH sizes + which we
  // suggest, instead of a single hard letter — confidence is lowest at the edge.
  P.boundaryHint = function () {
    if (this.mode !== 'estimate' || !this.result || !this.chart || !this.chart.sizes) return null;
    var conv = this.toMetric(this.inputs.height, this.inputs.weight);
    if (conv.h == null || conv.w == null) return null;
    var target = this.bodyTargetOrdinal(this.sizingSex(), conv.h, conv.w);
    if (target == null) return null;
    // A target RAISED by the below-chart guard (or the chart-edge clamp) is a
    // floor, not a body position: the shopper sits below the bracket entirely,
    // so the fractional position is an artefact. Don't claim they are
    // "between X and Y" (and don't re-offer the size the guard just ruled out).
    var rawTarget = this.bodyTargetOrdinal(this.sizingSex(), conv.h, conv.w, true);
    if (rawTarget != null && Math.abs(rawTarget - target) > 1e-9) return null;
    var sizes = this.chart.sizes.filter(function (s) { return sizeOrdinal(s) != null; })
      .sort(function (a, b) { return sizeOrdinal(a) - sizeOrdinal(b); });
    if (sizes.length < 2) return null;
    var band = (this.config.boundaryBand != null) ? this.config.boundaryBand : 0.15;
    if (!band) return null;
    var stds = this.sizeStandards();
    for (var i = 0; i < sizes.length - 1; i++) {
      var lo = this.effOrd(sizes[i], stds), hi = this.effOrd(sizes[i + 1], stds);
      if (target >= lo && target <= hi && hi > lo) {
        if (Math.abs((target - lo) / (hi - lo) - 0.5) > band) return null; // not borderline
        var rec = canonSize(this.result.recommended);
        if (rec === canonSize(sizes[i + 1])) return { lower: sizes[i], upper: sizes[i + 1], dir: 'up' };
        if (rec === canonSize(sizes[i])) return { lower: sizes[i], upper: sizes[i + 1], dir: 'down' };
        return null; // recommended isn't one of the two brackets (guard moved it) -> normal caption
      }
    }
    return null;
  };

  P.renderResult = function (scored, size) {
    // Always the recommended size (no size-switching / figure / size row).
    var token = scored.recommended;                    // chart token (availability + scoring entry)
    var display = this.displayLabelFor(token);          // product's real size label (shown + added to cart)
    this.selectedSize = display;
    var entry = scored.sizes.filter(function (r) { return r.size === token; })[0];
    // Borderline (between two sizes) -> name both + the suggestion. Else height/
    // weight path -> simple caption (hw_basis read directly so a missing string
    // never renders the raw key); manual path -> per-part verdict.
    var hwText = this.i18n.hw_basis;
    var bound = this.boundaryHint();
    var sentence;
    if (bound) {
      sentence = this.tpl(bound.dir === 'down' ? 'boundary_closer' : 'boundary_relaxed', {
        lower: this.displayLabelFor(bound.lower), upper: this.displayLabelFor(bound.upper), size: display
      });
    } else {
      sentence = (scored.basis === 'hw' && hwText) ? hwText : this.recommendedSentence(entry, display);
    }

    this.availability = this.computeAvailability();
    var oos = this.availability[token] === false;
    var bottomBtn = oos
      ? '<button type="button" class="button sr-continue is-disabled" data-sr-oos disabled aria-disabled="true">' + esc(this.t('out_of_stock')) + '</button>'
      : '<button type="button" class="button sr-continue" data-sr-addcart>' + esc(this.t('add_to_cart')) + '</button>';

    this.bodyEl.innerHTML =
      '<div class="sr-result sr-result--centered" tabindex="-1">' +
        '<div class="sr-result__main">' +
          '<div class="sr-result__size">' + esc(display) + '</div>' +
          '<div class="sr-result__head">' + esc(this.t('recommended_size')) + '</div>' +
          '<p class="sr-result__sentence">' + esc(sentence) + '</p>' +
        '</div>' +
        '<p class="sr-error" data-sr-error role="alert" hidden></p>' +
        '<div class="sr-result__actions">' +
          '<button type="button" class="sr-restart" data-sr-edit aria-label="' + esc(this.t('edit_measurements')) + '">' +
            '<svg class="sr-arrow-svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false" width="20" height="20"><path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M19 12H5M12 19l-7-7 7-7"/></svg>' +
          '</button>' +
          bottomBtn +
        '</div>' +
      '</div>';
    // injected-with-content live regions never announce — use the persistent one
    this.announce(this.tpl('your_size_is', { size: display }) + '. ' + sentence);
  };

  P.restart = function () {
    this.result = null; this.selectedSize = ''; this.mode = 'estimate'; this.stepIndex = 0;
    this.syncFormProps();
    this.render();
  };

  // ---- add to cart ---------------------------------------------------------
  // Resolve the variant for the chosen size in the product's currently-selected
  // other options (colour, etc.), then drive the real product form so the theme
  // handles the cart drawer / counters / errors.
  P.variantsData = function () {
    var el = document.querySelector('[data-all-variants]');
    if (!el) return null;
    try { var all = JSON.parse(el.textContent || '[]'); return (Array.isArray(all) && all.length) ? all : null; } catch (e) { return null; }
  };

  // Current-selection baseline (colour etc.) from the live form id, then the
  // page's initial selected variant. Returns { selOpts, sizeIdx }.
  P.matchContext = function (all) {
    var current = null;
    var form0 = document.querySelector('product-form form') || document.querySelector('form[action*="/cart/add"]');
    var idInput0 = form0 && form0.querySelector('[name="id"]');
    var currentId = idInput0 && idInput0.value ? String(idInput0.value) : '';
    if (currentId) current = all.filter(function (v) { return String(v.id) === currentId; })[0] || null;
    if (!current) {
      var selEl = document.querySelector('[data-selected-variant]');
      if (selEl) { try { current = JSON.parse(selEl.textContent || 'null'); } catch (e) { current = null; } }
    }
    var selOpts = (current && current.options) || [];
    var canon = (this.chart ? this.chart.sizes : []).map(canonSize);
    var sizeIdx = -1;
    for (var i = 0; i < selOpts.length; i++) {
      if (canon.indexOf(canonSize(selOpts[i])) !== -1) { sizeIdx = i; break; }
    }
    return { selOpts: selOpts, sizeIdx: sizeIdx };
  };

  // variants matching `size` in the current colour (other options). If sizeIdx
  // is unknown, match any option equal to the size token.
  P.variantsForSize = function (all, ctx, size) {
    var selOpts = ctx.selOpts, sizeIdx = ctx.sizeIdx, target = canonSize(size);
    return all.filter(function (v) {
      if (!v.options) return false;
      if (sizeIdx !== -1) {
        if (canonSize(v.options[sizeIdx]) !== target) return false;
        for (var j = 0; j < v.options.length; j++) {
          if (j === sizeIdx) continue;
          if (selOpts[j] !== undefined && v.options[j] !== selOpts[j]) return false;
        }
        return true;
      }
      for (var k = 0; k < v.options.length; k++) { if (canonSize(v.options[k]) === target) return true; }
      return false;
    });
  };

  P.findVariant = function (size) {
    var all = this.variantsData();
    if (!all) return null;
    var ctx = this.matchContext(all);
    var inColour = this.variantsForSize(all, ctx, size);
    var pick = inColour.filter(function (v) { return v.available; })[0] || inColour[0];
    if (pick) return pick;
    // fallback: any variant with that size (different colour)
    var target = canonSize(size);
    var any = all.filter(function (v) {
      if (!v.options) return false;
      for (var k = 0; k < v.options.length; k++) { if (canonSize(v.options[k]) === target) return true; }
      return false;
    });
    return any.filter(function (v) { return v.available; })[0] || any[0] || null;
  };

  P.findVariantId = function (size) { var v = this.findVariant(size); return v ? v.id : null; };

  // The chart's size token (e.g. "L-XL") can be spelled differently from the
  // product's actual option value (e.g. "L/XL"). Resolve the product's real
  // label for display + cart so the recommendation matches the PDP exactly.
  P.displayLabelFor = function (size) {
    var all = this.variantsData();
    if (!all) return size;
    var ctx = this.matchContext(all), target = canonSize(size), fallback = null;
    for (var i = 0; i < all.length; i++) {
      var v = all[i]; if (!v.options) continue;
      if (ctx.sizeIdx !== -1) {
        if (canonSize(v.options[ctx.sizeIdx]) !== target) continue;
        var sameColour = true;
        for (var j = 0; j < v.options.length; j++) {
          if (j === ctx.sizeIdx) continue;
          if (ctx.selOpts[j] !== undefined && v.options[j] !== ctx.selOpts[j]) { sameColour = false; break; }
        }
        if (sameColour) return v.options[ctx.sizeIdx];      // best: the current-colour label
        if (fallback == null) fallback = v.options[ctx.sizeIdx];
      } else {
        for (var k = 0; k < v.options.length; k++) {
          if (canonSize(v.options[k]) === target && fallback == null) fallback = v.options[k];
        }
      }
    }
    return fallback != null ? fallback : size;
  };

  // size -> available? (in the current colour). Unknown data -> assume available.
  P.computeAvailability = function () {
    var map = {};
    var sizes = this.chart ? this.chart.sizes : [];
    var all = this.variantsData();
    if (!all) { sizes.forEach(function (s) { map[s] = true; }); return map; }
    var ctx = this.matchContext(all), self = this;
    sizes.forEach(function (size) {
      var vs = self.variantsForSize(all, ctx, size);
      map[size] = vs.length ? vs.some(function (v) { return v.available; }) : false;
    });
    return map;
  };

  // Add the resolved variant directly via AJAX — never touches the on-page
  // variant picker (so the PDP selection/URL stays intact), then refresh the
  // theme's cart drawer the same way the product form does.
  P.addToCart = function () {
    if (this._adding) return; // double-tap guard — one click, one cart line
    var id = this.findVariantId(this.selectedSize);
    if (!id) { this.close(); return; }
    this._adding = true;
    var btn = this.bodyEl.querySelector('[data-sr-addcart]');
    if (btn) btn.disabled = true;
    track('sr_add_to_cart', { sr_size: this.selectedSize, sr_variant_id: String(id) });
    // Result always shows the recommended size (no size-switching), so chosen ==
    // recommended.
    this.ajaxAdd(id, this.orderProps(this.selectedSize, 'wizard'));
  };

  // Hidden line-item properties (_ prefix => not shown to the customer) for
  // attribution + return-rate calibration via Shopify Flow. `selected` is the
  // size being bought, `via` the button used: 'wizard' = the result screen's
  // Add to cart, 'pdp' = the page's own Add to cart (see syncFormProps).
  // PRIVACY: height/weight/sex are body data (PII). They were browser-only by
  // design; persisting them onto the order is opt-in analytics — ensure a lawful
  // basis + a retention policy. Stored normalised to metric (cm/kg) for analysis.
  P.orderProps = function (selected, via) {
    var conv = this.toMetric(this.inputs.height, this.inputs.weight);
    var askedShape = this.mode === 'estimate' && (!this.chart || this.chart.group !== 'bottom');
    var followed = canonSize(selected) !== '' && canonSize(selected) === canonSize(this.selectedSize);
    return {
      _find_my_size: 'true',
      _recommended_size: this.selectedSize,
      _selected_size: selected || '',
      _followed_recommendation: followed ? 'Yes' : 'No',
      _sr_via: via || '',
      _sr_sex: this.sex || '',
      _sr_height_cm: (conv.h != null) ? String(Math.round(conv.h)) : '',
      _sr_weight_kg: (conv.w != null) ? String(Math.round(conv.w)) : '',
      // calibration context: which path produced the size ('hw' = brand body
      // chart, 'fit' = garment-ease) and the shape answers that nudged it —
      // lets the returns audit separate model errors from input errors.
      // Build/Stomach are only ASKED (buildSteps) and only APPLIED
      // (bodyTargetOrdinal's shape nudge) for tops/dresses in estimate mode;
      // recording them elsewhere would put phantom signal in the dataset.
      // ajaxAdd drops empty values, so the props simply vanish there.
      _sr_mode: this.mode || '',
      _sr_basis: (this.result && this.result.basis) || '',
      _sr_build: askedShape ? (this.build || '') : '',
      _sr_stomach: askedShape ? (this.stomach || '') : '',
      // which corrections were in play for this size ("A:0.25" = cut on the A
      // standard, "fit:-0.3" = per-product correction) — so the next returns
      // audit can judge them separately from the body chart.
      _sr_adjust: this.adjustTag()
    };
  };

  // The product's size label on a variant (the option that is a chart size).
  P.sizeOfVariant = function (variant) {
    if (!variant || !variant.options || !this.chart) return '';
    var canon = this.chart.sizes.map(canonSize);
    for (var i = 0; i < variant.options.length; i++) {
      if (canon.indexOf(canonSize(variant.options[i])) !== -1) return variant.options[i];
    }
    return '';
  };

  // Most shoppers read the recommended size off the page and use the PDP's own
  // Add to cart, so once a recommendation exists the same properties ride along
  // on the product form as hidden inputs (dynamic checkout buttons read them
  // too) — saying whether the size being bought is the recommended one.
  // Re-run on every variant change and again right before the form submits.
  P.syncFormProps = function () {
    var info = this.productInfo;
    if (!info) return;
    var forms = info.querySelectorAll('form[data-type="add-to-cart-form"]');
    var i, j;
    for (i = 0; i < forms.length; i++) {
      var stale = forms[i].querySelectorAll('input[data-sr-prop]');
      for (j = 0; j < stale.length; j++) stale[j].parentNode.removeChild(stale[j]);
    }
    if (!this.result || !this.selectedSize) return;
    for (i = 0; i < forms.length; i++) {
      var variant = null;
      try { if (typeof info.getSelectedVariant === 'function') variant = info.getSelectedVariant(info); } catch (e) { variant = null; }
      if (!variant) {
        var idInput = forms[i].querySelector('[name="id"]');
        var id = idInput && idInput.value ? String(idInput.value) : '';
        variant = id ? (this.variantsData() || []).filter(function (v) { return String(v.id) === id; })[0] : null;
      }
      if (!variant) continue;
      var props = this.orderProps(this.sizeOfVariant(variant), 'pdp');
      var keys = Object.keys(props);
      for (j = 0; j < keys.length; j++) {
        if (props[keys[j]] == null || props[keys[j]] === '') continue;
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + keys[j] + ']';
        input.value = props[keys[j]];
        input.setAttribute('data-sr-prop', '');
        forms[i].appendChild(input);
      }
    }
  };

  P.adjustTag = function () {
    var out = [], cfg = this.config.fitStandard;
    var token = this.result && this.result.recommended;
    var std = token ? this.sizeStandards()[token] : '';
    var sh = (cfg && cfg.shift && std && cfg.shift[std]) || 0;
    if (sh) out.push(std + ':' + sh);
    var ps = this.productShift();
    if (ps) out.push('fit:' + ps);
    return out.join(' ');
  };

  // /cart/add error -> show it inside the modal (the item was NOT added; the
  // old redirect-to-cart dumped the user on a page with no item and no message)
  P.addFailed = function (msg) {
    this._adding = false;
    var btn = this.bodyEl.querySelector('[data-sr-addcart]');
    if (btn) btn.disabled = false;
    this.setError(msg || (window.cartStrings && window.cartStrings.error) || this.t('error_load'));
  };

  P.ajaxAdd = function (id, props) {
    var self = this;
    var cart = document.querySelector('cart-notification') ||
               document.querySelector('cart-drawer') ||
               document.querySelector('quick-cart-drawer');
    var url = (window.routes && window.routes.cart_add_url) ? window.routes.cart_add_url : '/cart/add';
    var cartUrl = (window.routes && window.routes.cart_url) ? window.routes.cart_url : '/cart';

    var fd = new FormData();
    fd.append('id', id);
    fd.append('quantity', '1');
    if (props) {
      Object.keys(props).forEach(function (k) {
        if (props[k] != null && props[k] !== '') fd.append('properties[' + k + ']', props[k]);
      });
    }
    if (cart && typeof cart.getSectionsToRender === 'function') {
      try {
        fd.append('sections', cart.getSectionsToRender().map(function (s) { return s.section; }).join(','));
        fd.append('sections_url', window.location.pathname);
      } catch (e) { /* ignore */ }
    }
    if (cart && typeof cart.setActiveElement === 'function') {
      try { cart.setActiveElement(document.body); } catch (e) { /* ignore */ }
    }

    fetch(url, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' }, body: fd })
      .then(function (r) { return r.json(); })
      .then(function (response) {
        if (response && (response.status || response.errors)) {
          // Shopify /cart/add error shape: { status: 422, message, description }
          var msg = (response.description && typeof response.description === 'string' && response.description) ||
                    (typeof response.message === 'string' && response.message) ||
                    (typeof response.errors === 'string' && response.errors) || '';
          self.addFailed(msg);
          return;
        }
        self._adding = false;
        self.close();
        if (!cart) { window.location.href = cartUrl; return; }
        try { if (typeof cart.renderContents === 'function') cart.renderContents(response); } catch (e) { /* ignore */ }
        try { if (typeof cart.open === 'function') cart.open(); } catch (e) { /* ignore */ }
        try { if (typeof updateCartCounters === 'function') updateCartCounters(); } catch (e) { /* ignore */ }
      })
      .catch(function () { self._adding = false; self.close(); window.location.href = cartUrl; });
  };

  // ---- sentences -----------------------------------------------------------
  P.partName = function (part) { return this.t('part_' + part); };

  P.namesList = function (parts) {
    var self = this, names = parts.map(function (p) { return self.partName(p); });
    if (names.length <= 1) return names[0] || '';
    var and = this.t('list_and'), sep = this.t('list_sep');
    if (names.length === 2) return names[0] + ' ' + and + ' ' + names[1];
    return names.slice(0, -1).join(sep) + sep + and + ' ' + names[names.length - 1];
  };

  P.recommendedSentence = function (entry, size) {
    var self = this, perfect = [], loose = [], tight = [];
    this.chart.parts.forEach(function (p) {
      var v = entry.byPart[p]; if (!v) return;
      if (v.verdict === 'perfect') perfect.push(p); else if (v.verdict === 'loose') loose.push(p); else tight.push(p);
    });
    var out = [];
    if (perfect.length) out.push(this.tpl('fit_well', { parts: this.namesList(perfect), size: size }));
    if (loose.length) out.push(this.tpl('caveat_loose', { parts: this.namesList(loose) }));
    if (tight.length) out.push(this.tpl('caveat_tight', { parts: this.namesList(tight) }));
    if (loose.length) out.push(this.tpl('caveat_check', { part: this.namesList([loose[0]]) }));
    if (!out.length) out.push(this.tpl('fit_well', { parts: this.namesList(this.chart.parts), size: size }));
    return out.join(' ');
  };


  // ---- small helpers -------------------------------------------------------
  P.segBtn = function (name, val, label, on) {
    return '<button type="button" class="sr-seg__btn' + (on ? ' is-selected' : '') + '" role="radio" aria-checked="' + (on ? 'true' : 'false') + '" data-' + name + '="' + val + '">' + esc(label) + '</button>';
  };

  P.bindSeg = function (sel, onSelect) {
    var container = this.bodyEl.querySelector(sel); if (!container) return;
    var btns = Array.prototype.slice.call(container.querySelectorAll('[role="radio"]'));
    if (!btns.length) return;
    function setActive(btn, fire) {
      btns.forEach(function (b) {
        var on = b === btn;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
      if (fire) {
        var val = null, ds = btn.dataset;
        Object.keys(ds).forEach(function (k) { if (k !== '') val = ds[k]; });
        onSelect(val);
      }
    }
    // WAI-ARIA radio-group pattern: one tab stop (roving tabindex) + Arrow keys
    var initial = btns.filter(function (b) { return b.getAttribute('aria-checked') === 'true'; })[0] || btns[0];
    btns.forEach(function (b) { b.tabIndex = (b === initial) ? 0 : -1; });
    btns.forEach(function (btn, idx) {
      btn.addEventListener('click', function () { setActive(btn, true); });
      btn.addEventListener('keydown', function (event) {
        var dir = 0;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') dir = 1;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') dir = -1;
        else return;
        event.preventDefault();
        var next = btns[(idx + dir + btns.length) % btns.length];
        next.focus();
        setActive(next, true);
      });
    });
  };

  // ---- persistence (local only) -------------------------------------------
  P.persist = function () {
    try {
      // Persist sex only when the USER picked it — a product-derived sex (e.g.
      // browsing a women's gift) must not silently follow the shopper onto
      // unisex products; preserve any previously user-chosen value instead.
      var sexToSave = this._sexChosen ? this.sex : (this._restoredSex || '');
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        sex: sexToSave, unitSystem: this.unitSystem, inputs: this.inputs,
        manualValues: this.manualValues, build: this.build, stomach: this.stomach
      }));
    } catch (e) { /* ignore */ }
  };

  P.restore = function () {
    var saved;
    try { saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null'); } catch (e) { saved = null; }
    if (!saved) return;
    this._restoredSex = saved.sex || '';
    if (saved.sex && !this.sex) this.sex = saved.sex;
    if (saved.unitSystem) this.unitSystem = saved.unitSystem;
    if (saved.inputs) this.inputs = saved.inputs;
    if (saved.manualValues) this.manualValues = saved.manualValues;
    if (saved.build) this.build = saved.build;
    if (saved.stomach) this.stomach = saved.stomach;
    // migrate a legacy imperial profile (height saved as ONE total-inches
    // number) to the ft+in pair
    if (this.unitSystem === 'imperial' && this.inputs && this.inputs.height &&
        this.inputs.height_ft == null && this.inputs.height_in == null) {
      var t = num(this.inputs.height);
      if (t != null) {
        var ft = Math.floor(t / 12);
        this.inputs.height_ft = String(ft);
        this.inputs.height_in = String(Math.round((t - ft * 12) * 2) / 2);
      }
    }
  };

  window.SizeRecommender = SizeRecommender;
})();
